import React, { useState, useEffect } from 'react';
import { motion } from "framer-motion";
import { Star, Send, CheckCircle, User } from 'lucide-react';
import { Button } from './button';
import { Input } from './input';
import { Textarea } from './textarea';
import { Label } from './label';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Testimonial Card Component
const TestimonialCard = ({ testimonial, duration = 15 }) => {
  const { name, text, destination, rating } = testimonial;
  
  return (
    <motion.div 
      whileHover={{ 
        scale: 1.02,
        y: -5,
        transition: { type: "spring", stiffness: 400, damping: 17 }
      }}
      className="p-6 sm:p-8 rounded-2xl border border-[#e5e7eb] shadow-lg bg-white max-w-sm w-full cursor-default group"
    >
      {/* Stars */}
      <div className="flex gap-1 mb-4">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star 
            key={star}
            className={`w-5 h-5 ${star <= rating ? 'text-[#e9d8a6] fill-[#e9d8a6]' : 'text-gray-200'}`}
          />
        ))}
      </div>
      
      {/* Text */}
      <p className="text-[#6c757d] leading-relaxed mb-6 italic">
        "{text}"
      </p>
      
      {/* Author */}
      <div className="flex items-center gap-3">
        <div className="w-12 h-12 bg-gradient-to-br from-[#005f73] to-[#0a9396] rounded-full flex items-center justify-center">
          <span className="text-white font-semibold text-lg">{name.charAt(0)}</span>
        </div>
        <div>
          <p className="font-semibold text-[#2b2d42]">{name}</p>
          <p className="text-sm text-[#0a9396]">Viagem: {destination}</p>
        </div>
      </div>
    </motion.div>
  );
};

// Testimonials Column with infinite scroll animation
const TestimonialsColumn = ({ testimonials, duration = 15, className = "" }) => {
  if (testimonials.length === 0) return null;
  
  return (
    <div className={`overflow-hidden ${className}`}>
      <motion.div
        animate={{
          translateY: "-50%",
        }}
        transition={{
          duration: duration,
          repeat: Infinity,
          ease: "linear",
          repeatType: "loop",
        }}
        className="flex flex-col gap-6 pb-6"
      >
        {[...testimonials, ...testimonials].map((testimonial, index) => (
          <TestimonialCard 
            key={`${testimonial.id}-${index}`} 
            testimonial={testimonial}
          />
        ))}
      </motion.div>
    </div>
  );
};

// Testimonial Form Component
const TestimonialForm = ({ onSuccess }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    destination: '',
    text: '',
    rating: 5
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      const response = await fetch(`${API_URL}/api/testimonials`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        throw new Error('Erro ao enviar testemunho');
      }

      setSubmitted(true);
      setFormData({ name: '', email: '', destination: '', text: '', rating: 5 });
      if (onSuccess) onSuccess();
    } catch (err) {
      setError('Ocorreu um erro. Tente novamente.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <motion.div 
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-2xl p-8 shadow-lg text-center"
      >
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <CheckCircle className="w-8 h-8 text-green-600" />
        </div>
        <h3 className="font-heading text-2xl font-bold text-[#2b2d42] mb-2">
          Obrigado!
        </h3>
        <p className="text-[#6c757d] mb-4">
          O seu testemunho foi enviado e será publicado após aprovação.
        </p>
        <Button
          onClick={() => setSubmitted(false)}
          variant="outline"
          className="border-[#005f73] text-[#005f73] hover:bg-[#005f73] hover:text-white"
        >
          Enviar Outro
        </Button>
      </motion.div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="bg-white rounded-2xl p-6 sm:p-8 shadow-lg space-y-4">
      <h3 className="font-heading text-xl font-bold text-[#2b2d42] mb-4">
        Partilhe a Sua Experiência
      </h3>
      
      <div className="grid sm:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="name">Nome *</Label>
          <Input
            id="name"
            value={formData.name}
            onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
            placeholder="O seu nome"
            required
            data-testid="testimonial-name"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="email">Email *</Label>
          <Input
            id="email"
            type="email"
            value={formData.email}
            onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
            placeholder="seu@email.com"
            required
            data-testid="testimonial-email"
          />
        </div>
      </div>
      
      <div className="space-y-2">
        <Label htmlFor="destination">Destino da Viagem *</Label>
        <Input
          id="destination"
          value={formData.destination}
          onChange={(e) => setFormData(prev => ({ ...prev, destination: e.target.value }))}
          placeholder="Ex: Maldivas, Paris..."
          required
          data-testid="testimonial-destination"
        />
      </div>
      
      <div className="space-y-2">
        <Label>Avaliação *</Label>
        <div className="flex gap-2">
          {[1, 2, 3, 4, 5].map((star) => (
            <button
              key={star}
              type="button"
              onClick={() => setFormData(prev => ({ ...prev, rating: star }))}
              className="p-1 hover:scale-110 transition-transform"
              data-testid={`testimonial-star-${star}`}
            >
              <Star 
                className={`w-8 h-8 ${star <= formData.rating ? 'text-[#e9d8a6] fill-[#e9d8a6]' : 'text-gray-300'}`}
              />
            </button>
          ))}
        </div>
      </div>
      
      <div className="space-y-2">
        <Label htmlFor="text">A Sua Experiência *</Label>
        <Textarea
          id="text"
          value={formData.text}
          onChange={(e) => setFormData(prev => ({ ...prev, text: e.target.value }))}
          placeholder="Conte-nos como foi a sua viagem..."
          rows={4}
          required
          data-testid="testimonial-text"
        />
      </div>
      
      {error && (
        <p className="text-red-500 text-sm">{error}</p>
      )}
      
      <Button
        type="submit"
        disabled={isSubmitting}
        className="w-full bg-[#005f73] hover:bg-[#0a9396] text-white rounded-full py-6"
        data-testid="testimonial-submit"
      >
        {isSubmitting ? (
          'A enviar...'
        ) : (
          <>
            <Send className="w-5 h-5 mr-2" />
            Enviar Testemunho
          </>
        )}
      </Button>
      
      <p className="text-xs text-[#6c757d] text-center">
        * O seu testemunho será publicado após aprovação
      </p>
    </form>
  );
};

// Main Testimonials Section
const TestimonialsSection = () => {
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTestimonials();
  }, []);

  const fetchTestimonials = async () => {
    try {
      const response = await fetch(`${API_URL}/api/testimonials`);
      if (response.ok) {
        const data = await response.json();
        setTestimonials(data);
      }
    } catch (err) {
      console.error('Error fetching testimonials:', err);
    } finally {
      setLoading(false);
    }
  };

  // Split testimonials into columns
  const firstColumn = testimonials.filter((_, i) => i % 3 === 0);
  const secondColumn = testimonials.filter((_, i) => i % 3 === 1);
  const thirdColumn = testimonials.filter((_, i) => i % 3 === 2);

  // Placeholder testimonials when no real ones exist
  const placeholderTestimonials = [
    {
      id: 'placeholder-1',
      name: 'Cliente Satisfeito',
      destination: 'Maldivas',
      text: 'Em breve partilharemos aqui as experiências dos nossos clientes.',
      rating: 5
    },
    {
      id: 'placeholder-2',
      name: 'Cliente Satisfeito',
      destination: 'Santorini',
      text: 'Seja o primeiro a partilhar a sua experiência connosco!',
      rating: 5
    },
    {
      id: 'placeholder-3',
      name: 'Cliente Satisfeito',
      destination: 'Dubai',
      text: 'A sua opinião é muito importante para nós.',
      rating: 5
    }
  ];

  const displayTestimonials = testimonials.length > 0 ? testimonials : placeholderTestimonials;
  const displayFirstColumn = displayTestimonials.filter((_, i) => i % 3 === 0);
  const displaySecondColumn = displayTestimonials.filter((_, i) => i % 3 === 1);
  const displayThirdColumn = displayTestimonials.filter((_, i) => i % 3 === 2);

  return (
    <section 
      className="py-20 sm:py-28 bg-[#f8f9fa] relative overflow-hidden"
      data-testid="testimonials-section"
    >
      <motion.div 
        initial={{ opacity: 0, y: 50 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.15 }}
        transition={{ duration: 0.8 }}
        className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8"
      >
        {/* Header */}
        <div className="text-center mb-16">
          <span className="text-[#0a9396] font-medium text-sm uppercase tracking-wider">Testemunhos</span>
          <h2 className="font-heading text-4xl sm:text-5xl font-bold text-[#2b2d42] mt-4 mb-4">
            O Que Dizem os Nossos Clientes
          </h2>
          <p className="text-[#6c757d] max-w-2xl mx-auto text-lg">
            Descubra as experiências de quem já viajou connosco
          </p>
        </div>

        {/* Testimonials Grid */}
        <div className="grid lg:grid-cols-3 gap-8 mb-16">
          {/* Scrolling Testimonials */}
          <div 
            className="lg:col-span-2 flex justify-center gap-6 [mask-image:linear-gradient(to_bottom,transparent,black_10%,black_90%,transparent)] max-h-[600px] overflow-hidden"
          >
            {displayFirstColumn.length > 0 && (
              <TestimonialsColumn testimonials={displayFirstColumn} duration={18} />
            )}
            {displaySecondColumn.length > 0 && (
              <TestimonialsColumn testimonials={displaySecondColumn} duration={22} className="hidden md:block" />
            )}
          </div>

          {/* Form */}
          <div className="lg:col-span-1">
            <TestimonialForm onSuccess={fetchTestimonials} />
          </div>
        </div>
      </motion.div>
    </section>
  );
};

export default TestimonialsSection;
